import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Bookmark,
  BookmarkX,
  Copy,
  Facebook,
  Instagram,
  Megaphone,
  Wand2,
  Check,
  CheckCircle2,
  Clock,
  Pencil,
  RotateCw,
} from "lucide-react";

const PLATFORM_META = {
  facebook: { label: "Facebook", icon: Facebook, hint: "Long-form" },
  x: { label: "X", icon: Megaphone, hint: "≤ 280 chars" },
  instagram: { label: "Instagram", icon: Instagram, hint: "Caption" },
};

const QUICK_INSTRUCTIONS = [
  "Make it shorter",
  "More aggressive",
  "More folksy",
  "Less formal",
  "Add a clear call to action",
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function keyOf(p) {
  return `${p.history_id}::${p.platform}`;
}

export default function SavedPosts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | queued | used
  // Per-item editing state
  const [editingKey, setEditingKey] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Per-item Suggest-Edits state
  const [refineKey, setRefineKey] = useState(null);
  const [refineInstr, setRefineInstr] = useState("");
  const [refining, setRefining] = useState(false);
  const [proposed, setProposed] = useState(null); // { original, revised }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts/saved");
      setItems(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  };

  const patch = async (p, body) => {
    return api.patch(`/posts/history/${p.history_id}/drafts/${p.platform}`, body);
  };

  const toggleUsed = async (p) => {
    const next = !p.used;
    try {
      await patch(p, { used: next });
      setItems((prev) =>
        prev.map((x) =>
          keyOf(x) === keyOf(p)
            ? { ...x, used: next, used_at: next ? new Date().toISOString() : null }
            : x
        )
      );
      toast.success(next ? "Marked as posted" : "Moved back to queue");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const unsave = async (p) => {
    try {
      await patch(p, { saved: false });
      setItems((prev) => prev.filter((x) => keyOf(x) !== keyOf(p)));
      toast.success("Removed from Saved Posts");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openEdit = (p) => {
    setEditingKey(keyOf(p));
    setEditText(p.text);
    // close refine panel if open on the same card
    if (refineKey === keyOf(p)) {
      setRefineKey(null);
      setProposed(null);
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditText("");
  };

  const saveEdit = async (p) => {
    const text = (editText || "").trim();
    if (!text) {
      toast.error("Text can't be empty.");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await patch(p, { text });
      setItems((prev) =>
        prev.map((x) =>
          keyOf(x) === keyOf(p)
            ? { ...x, text: data.text, last_edited_at: data.last_edited_at }
            : x
        )
      );
      toast.success("Edit saved");
      cancelEdit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const openRefine = (p) => {
    setRefineKey(keyOf(p));
    setRefineInstr("");
    setProposed(null);
    if (editingKey === keyOf(p)) cancelEdit();
  };

  const closeRefine = () => {
    setRefineKey(null);
    setRefineInstr("");
    setProposed(null);
  };

  const submitRefine = async (p) => {
    const instr = (refineInstr || "").trim();
    if (!instr) {
      toast.error("Tell us what to change.");
      return;
    }
    setRefining(true);
    setProposed(null);
    try {
      const { data } = await api.post("/posts/refine", {
        original: p.text,
        instruction: instr,
        platform: p.platform,
      });
      setProposed({ original: p.text, revised: data.revised });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefining(false);
    }
  };

  const acceptProposed = async (p) => {
    if (!proposed) return;
    try {
      await patch(p, { text: proposed.revised });
      setItems((prev) =>
        prev.map((x) =>
          keyOf(x) === keyOf(p)
            ? { ...x, text: proposed.revised, last_edited_at: new Date().toISOString() }
            : x
        )
      );
      toast.success("Edit accepted");
      closeRefine();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = items.filter((x) =>
    filter === "all" ? true : filter === "used" ? x.used : !x.used
  );
  const queuedCount = items.filter((x) => !x.used).length;
  const usedCount = items.filter((x) => x.used).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Social Media</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Saved Posts</h1>
        <p className="text-slate-600 mt-2">
          The posts you&rsquo;ve deliberately kept. Copy them, refine them with Suggest Edits, and mark them used as you publish — so you always know what&rsquo;s still in the queue.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="mt-6 inline-flex bg-slate-100 rounded-lg p-1" data-testid="saved-filter">
        {[
          { v: "all", label: `All (${items.length})` },
          { v: "queued", label: `Queued (${queuedCount})` },
          { v: "used", label: `Used (${usedCount})` },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setFilter(t.v)}
            data-testid={`saved-filter-${t.v}`}
            className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
              filter === t.v ? "bg-white text-flag-navy shadow-sm" : "text-slate-600 hover:text-flag-navy"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="mt-10 text-slate-500" data-testid="saved-loading">Loading…</div>}
      {error && (
        <div className="mt-6 text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="saved-error">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-8 bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="saved-empty">
          <Bookmark className="h-8 w-8 text-slate-400 mx-auto" />
          <h2 className="font-display text-xl font-bold text-flag-navy mt-3">No saved posts yet</h2>
          <p className="text-slate-600 mt-1">
            On any generated post, tap <span className="font-semibold">Save</span> to keep it here for later.
          </p>
          <Link to="/generator" data-testid="saved-empty-cta">
            <Button className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold">Open Post Generator</Button>
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && filtered.length === 0 && (
        <div className="mt-8 text-slate-500 text-sm" data-testid="saved-filter-empty">
          Nothing here yet for this filter.
        </div>
      )}

      <ul className="mt-6 space-y-4">
        {filtered.map((p) => {
          const meta = PLATFORM_META[p.platform] || PLATFORM_META.facebook;
          const Icon = meta.icon;
          const k = keyOf(p);
          const isEditing = editingKey === k;
          const isRefining = refineKey === k;
          return (
            <li
              key={k}
              data-testid={`saved-item-${p.history_id}-${p.platform}`}
              className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-flag-navy">{meta.label}</div>
                    <div className="text-xs text-slate-500">
                      Saved {formatDate(p.saved_at)}
                      {p.last_edited_at && (
                        <> · edited {formatDate(p.last_edited_at)}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.used ? (
                    <span
                      data-testid={`saved-status-used-${p.history_id}-${p.platform}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2.5 py-1 rounded-full"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Used
                    </span>
                  ) : (
                    <span
                      data-testid={`saved-status-queued-${p.history_id}-${p.platform}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-2.5 py-1 rounded-full"
                    >
                      <Clock className="h-3 w-3" /> Queued
                    </span>
                  )}
                </div>
              </div>

              {p.moment && (
                <div className="mt-3 text-xs text-slate-500 line-clamp-1">
                  Originally generated from: <span className="text-slate-700">{p.moment}</span>
                </div>
              )}

              {!isEditing ? (
                <p
                  data-testid={`saved-text-${p.history_id}-${p.platform}`}
                  className="mt-4 text-slate-800 whitespace-pre-wrap leading-relaxed"
                >
                  {p.text}
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Edit text</Label>
                  <Textarea
                    rows={p.platform === "facebook" ? 7 : 4}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    data-testid={`saved-edit-text-${p.history_id}-${p.platform}`}
                    className="text-base"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => saveEdit(p)}
                      disabled={savingEdit}
                      data-testid={`saved-edit-save-${p.history_id}-${p.platform}`}
                      className="h-9 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                    >
                      <Check className="h-4 w-4 mr-1.5" />
                      {savingEdit ? "Saving…" : "Save changes"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={cancelEdit}
                      data-testid={`saved-edit-cancel-${p.history_id}-${p.platform}`}
                      className="h-9 px-4 border-2 font-semibold"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Action row */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => copy(p.text)}
                  data-testid={`saved-copy-${p.history_id}-${p.platform}`}
                  className="h-9 border-2 font-semibold"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
                {!isEditing && (
                  <Button
                    variant="outline"
                    onClick={() => openEdit(p)}
                    data-testid={`saved-edit-open-${p.history_id}-${p.platform}`}
                    className="h-9 border-2 font-semibold"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                )}
                <Button
                  variant={isRefining ? "secondary" : "outline"}
                  onClick={() => (isRefining ? closeRefine() : openRefine(p))}
                  data-testid={`saved-refine-open-${p.history_id}-${p.platform}`}
                  className={`h-9 font-semibold ${isRefining ? "bg-flag-navy text-white hover:bg-flag-navy-2" : "border-2"}`}
                >
                  <Wand2 className="h-3.5 w-3.5 mr-1.5" /> {isRefining ? "Close" : "Suggest edit"}
                </Button>
                <Button
                  variant={p.used ? "outline" : "default"}
                  onClick={() => toggleUsed(p)}
                  data-testid={`saved-used-toggle-${p.history_id}-${p.platform}`}
                  className={`h-9 font-semibold ${p.used ? "border-2 text-slate-700" : "bg-green-700 hover:bg-green-800 text-white"}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  {p.used ? "Mark as queued" : "Mark as used"}
                </Button>
                <div className="ml-auto">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        data-testid={`saved-unsave-${p.history_id}-${p.platform}`}
                        className="h-9 text-flag-red hover:text-flag-red-hover hover:bg-red-50 font-semibold"
                      >
                        <BookmarkX className="h-4 w-4 mr-1.5" /> Remove
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove from Saved Posts?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The post will be unstarred. The original draft set will still live in your History.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`saved-unsave-cancel-${p.history_id}-${p.platform}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => unsave(p)}
                          data-testid={`saved-unsave-confirm-${p.history_id}-${p.platform}`}
                          className="bg-flag-red hover:bg-flag-red-hover"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {isRefining && (
                <div
                  className="mt-4 border-t border-slate-100 pt-4 space-y-3"
                  data-testid={`saved-refine-panel-${p.history_id}-${p.platform}`}
                >
                  <Label className="text-sm font-semibold text-slate-800">What should change?</Label>
                  <Textarea
                    rows={2}
                    value={refineInstr}
                    onChange={(e) => setRefineInstr(e.target.value)}
                    placeholder='e.g. "Make it shorter and mention the parade"'
                    className="text-base"
                    data-testid={`saved-refine-instr-${p.history_id}-${p.platform}`}
                  />
                  <div className="flex flex-wrap gap-2">
                    {QUICK_INSTRUCTIONS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setRefineInstr(q)}
                        data-testid={`saved-refine-chip-${p.history_id}-${p.platform}-${q.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => submitRefine(p)}
                      disabled={refining || !refineInstr.trim()}
                      data-testid={`saved-refine-submit-${p.history_id}-${p.platform}`}
                      className="h-10 px-4 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
                    >
                      <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                      {refining ? "Revising…" : proposed ? "Re-run" : "Suggest edit"}
                    </Button>
                    {proposed && (
                      <span className="text-xs text-slate-500">Compare below, then accept or refine again.</span>
                    )}
                  </div>

                  {proposed && (
                    <div
                      className="mt-3 grid sm:grid-cols-2 gap-3"
                      data-testid={`saved-refine-compare-${p.history_id}-${p.platform}`}
                    >
                      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Original</div>
                        <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{proposed.original}</p>
                      </div>
                      <div className="border-2 border-flag-red rounded-lg p-3 bg-red-50/40">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-flag-red">Proposed edit</div>
                        <p className="mt-2 text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">{proposed.revised}</p>
                      </div>
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
                        <Button
                          onClick={() => acceptProposed(p)}
                          data-testid={`saved-refine-accept-${p.history_id}-${p.platform}`}
                          className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                        >
                          <Check className="h-4 w-4 mr-1.5" /> Accept edit
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setProposed(null)}
                          data-testid={`saved-refine-keep-${p.history_id}-${p.platform}`}
                          className="h-10 px-4 border-2 font-semibold"
                        >
                          <RotateCw className="h-4 w-4 mr-1.5" /> Keep refining
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={closeRefine}
                          data-testid={`saved-refine-discard-${p.history_id}-${p.platform}`}
                          className="h-10 px-3 font-semibold text-slate-600 hover:text-flag-red"
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
